import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from "@mui/material";
import { DateTime } from "luxon";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DialogHeaderWithClose } from "../Dialog/DialogHeaderWithClose.component";
import {
  SeriesBookingScope,
  SeriesScopeTargetSet,
  SeriesScopeTargetsResponse
} from "../../features/Schedule/series-booking.types";
import { isSeriesDialogBackdropCloseReason } from "./functions/is-series-dialog-backdrop-close-reason.function";
import { useSeriesScopeTargets } from "./hooks/use-series-scope-targets.hook";

type P = {
  open: boolean;
  selectedDateIso: string;
  timezone: string;
  /** Opened booking. The preview is loaded for this id while the dialog is open. */
  bookingId: number;
  bookingType: string;
  titleKey: string;
  bodyKey: string;
  confirmLabelKey: string;
  /** Shown when the selected scope has no days left. */
  emptyScopeKey?: string;
  confirmColor?: "error" | "primary";
  onClose: () => void;
  onConfirm: (scope: SeriesBookingScope) => void;
};

const SERIES_SCOPE_VISIBLE_ROWS = 3;
const SERIES_SCOPE_CHIP_HEIGHT = "2.5625rem";
const SERIES_SCOPE_CHIP_GAP = 0.75;

const seriesScopeOptionSx = {
  alignItems: "flex-start",
  display: "flex",
  mr: 0,
  width: "100%",
  "& .MuiFormControlLabel-label": { flex: 1, minWidth: 0 }
} as const;

function formatSeriesDay(start: string | null, timezone: string): string {
  if (!start) {
    return "";
  }
  return DateTime.fromISO(start).setZone(timezone).toFormat("ccc, d LLL");
}

function targetSetForScope(
  scope: SeriesBookingScope,
  targets: SeriesScopeTargetsResponse | undefined
): SeriesScopeTargetSet | undefined {
  if (!targets) {
    return undefined;
  }
  if (scope === SeriesBookingScope.OCCURRENCE) {
    return targets.occurrence;
  }
  if (scope === SeriesBookingScope.THIS_AND_FUTURE) {
    return targets.thisAndFuture;
  }
  return targets.entireSeries;
}

type ScopeOptionLabelProps = {
  title: string;
  description: React.ReactNode;
};

function ScopeOptionLabel({ title, description }: Readonly<ScopeOptionLabelProps>) {
  return (
    <Box sx={{ py: 0.5 }}>
      <Typography component="span" variant="body1" display="block">
        {title}
      </Typography>
      <Typography component="span" variant="body2" color="text.secondary" display="block">
        {description}
      </Typography>
    </Box>
  );
}

type ScopeSummaryProps = {
  targetSet: SeriesScopeTargetSet | undefined;
  timezone: string;
  fallback: string;
  emptyLabel: string;
  /** The option title already names the single day, so the summary keeps the hint. */
  keepSingleDateInTitle?: boolean;
};

function ScopeSummary({
  targetSet,
  timezone,
  fallback,
  emptyLabel,
  keepSingleDateInTitle = false
}: Readonly<ScopeSummaryProps>) {
  const { t } = useTranslation();
  if (!targetSet) {
    return <>{fallback}</>;
  }
  if (targetSet.count === 0) {
    return <>{emptyLabel}</>;
  }
  const first = formatSeriesDay(targetSet.firstStart, timezone);
  if (targetSet.count === 1) {
    return <>{keepSingleDateInTitle ? fallback : first}</>;
  }
  const last = formatSeriesDay(targetSet.lastStart, timezone);
  return (
    <>
      {targetSet.count} {t("_schedule_seriesScopeDays")}
      {", "}
      {first} {t("_schedule_seriesScopeThrough")} {last}
    </>
  );
}

type ScopeDayListProps = {
  targetSet: SeriesScopeTargetSet | undefined;
  timezone: string;
  selected: boolean;
  testId: string;
};

function ScopeDayList({ targetSet, timezone, selected, testId }: Readonly<ScopeDayListProps>) {
  if (!selected || !targetSet || targetSet.days.length < 2) {
    return null;
  }
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignContent: "flex-start",
        gap: SERIES_SCOPE_CHIP_GAP,
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        maxHeight: theme =>
          `calc(${SERIES_SCOPE_CHIP_HEIGHT} * ${SERIES_SCOPE_VISIBLE_ROWS} + ${theme.spacing(
            SERIES_SCOPE_CHIP_GAP
          )} * ${SERIES_SCOPE_VISIBLE_ROWS - 1})`,
        overflowY: "auto",
        mt: 0.5,
        pr: 0.5
      }}
      data-testid={testId}
    >
      {targetSet.days.map(day => {
        const parsed = DateTime.fromISO(day.start).setZone(timezone);
        return (
          <Box
            key={day.id}
            sx={{
              minWidth: "4.5rem",
              height: SERIES_SCOPE_CHIP_HEIGHT,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              px: 1,
              borderRadius: 1,
              bgcolor: "action.hover"
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
              {parsed.toFormat("ccc")}
            </Typography>
            <Typography variant="body2" display="block" lineHeight={1.3}>
              {parsed.toFormat("d LLL")}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export function ScheduleSeriesScopeDialog({
  open,
  selectedDateIso,
  timezone,
  bookingId,
  bookingType,
  titleKey,
  bodyKey,
  confirmLabelKey,
  emptyScopeKey = "_schedule_seriesCancelNothingLeft",
  confirmColor = "primary",
  onClose,
  onConfirm
}: Readonly<P>) {
  const { t } = useTranslation();
  const [scope, setScope] = useState<SeriesBookingScope>(SeriesBookingScope.OCCURRENCE);
  const targetsQuery = useSeriesScopeTargets(bookingId, bookingType, open);
  const previewReady =
    targetsQuery.data != null && !targetsQuery.isFetching && !targetsQuery.isError;
  const selectedSet = targetSetForScope(scope, targetsQuery.data);
  const confirmDisabled = !previewReady || selectedSet == null || selectedSet.count === 0;
  const emptyLabel = t(emptyScopeKey);

  useEffect(() => {
    if (open) {
      setScope(SeriesBookingScope.OCCURRENCE);
    }
  }, [open]);

  const formattedDate = DateTime.fromISO(selectedDateIso).setZone(timezone).toFormat("ccc, d LLL");

  const handleDialogClose = (_event: object, reason: "backdropClick" | "escapeKeyDown") => {
    if (isSeriesDialogBackdropCloseReason(reason)) {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      aria-labelledby="schedule-series-scope-title"
      data-testid="schedule-series-scope-dialog"
    >
      <DialogHeaderWithClose
        titleId="schedule-series-scope-title"
        title={t(titleKey)}
        onClose={onClose}
        closeTestId="schedule-series-scope-close"
      />
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{t(bodyKey)}</DialogContentText>
        <FormControl fullWidth>
          <RadioGroup
            value={scope}
            onChange={event => setScope(event.target.value as SeriesBookingScope)}
            data-testid="schedule-series-scope-options"
          >
            <FormControlLabel
              value={SeriesBookingScope.OCCURRENCE}
              control={<Radio />}
              sx={{ ...seriesScopeOptionSx, mb: 1 }}
              label={
                <Box sx={{ width: "100%", minWidth: 0 }}>
                  <ScopeOptionLabel
                    title={t("_schedule_seriesScopeOnlyThisDay", { date: formattedDate })}
                    description={
                      <ScopeSummary
                        targetSet={targetSetForScope(
                          SeriesBookingScope.OCCURRENCE,
                          targetsQuery.data
                        )}
                        timezone={timezone}
                        fallback={t("_schedule_seriesScopeOnlyThisDayHint")}
                        emptyLabel={emptyLabel}
                        keepSingleDateInTitle
                      />
                    }
                  />
                  <ScopeDayList
                    targetSet={targetSetForScope(SeriesBookingScope.OCCURRENCE, targetsQuery.data)}
                    timezone={timezone}
                    selected={scope === SeriesBookingScope.OCCURRENCE}
                    testId="schedule-series-scope-days-occurrence"
                  />
                </Box>
              }
              data-testid="schedule-series-scope-occurrence"
            />
            <FormControlLabel
              value={SeriesBookingScope.THIS_AND_FUTURE}
              control={<Radio />}
              sx={{ ...seriesScopeOptionSx, mb: 1 }}
              label={
                <Box sx={{ width: "100%", minWidth: 0 }}>
                  <ScopeOptionLabel
                    title={t("_schedule_seriesScopeThisAndLater")}
                    description={
                      <ScopeSummary
                        targetSet={targetSetForScope(
                          SeriesBookingScope.THIS_AND_FUTURE,
                          targetsQuery.data
                        )}
                        timezone={timezone}
                        fallback={t("_schedule_seriesScopeThisAndLaterHint")}
                        emptyLabel={emptyLabel}
                      />
                    }
                  />
                  <ScopeDayList
                    targetSet={targetSetForScope(
                      SeriesBookingScope.THIS_AND_FUTURE,
                      targetsQuery.data
                    )}
                    timezone={timezone}
                    selected={scope === SeriesBookingScope.THIS_AND_FUTURE}
                    testId="schedule-series-scope-days-this-and-future"
                  />
                </Box>
              }
              data-testid="schedule-series-scope-this-and-future"
            />
            <FormControlLabel
              value={SeriesBookingScope.ENTIRE_SERIES}
              control={<Radio />}
              sx={seriesScopeOptionSx}
              label={
                <Box sx={{ width: "100%", minWidth: 0 }}>
                  <ScopeOptionLabel
                    title={t("_schedule_seriesScopeEntireSeries")}
                    description={
                      <ScopeSummary
                        targetSet={targetSetForScope(
                          SeriesBookingScope.ENTIRE_SERIES,
                          targetsQuery.data
                        )}
                        timezone={timezone}
                        fallback={t("_schedule_seriesScopeEntireSeriesHint")}
                        emptyLabel={emptyLabel}
                      />
                    }
                  />
                  <ScopeDayList
                    targetSet={targetSetForScope(
                      SeriesBookingScope.ENTIRE_SERIES,
                      targetsQuery.data
                    )}
                    timezone={timezone}
                    selected={scope === SeriesBookingScope.ENTIRE_SERIES}
                    testId="schedule-series-scope-days-entire-series"
                  />
                </Box>
              }
              data-testid="schedule-series-scope-entire-series"
            />
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="schedule-series-scope-cancel" color="secondary">
          {t("Cancel")}
        </Button>
        <Button
          onClick={() => onConfirm(scope)}
          variant="contained"
          color={confirmColor}
          disabled={confirmDisabled}
          data-testid="schedule-series-scope-confirm"
        >
          {t(confirmLabelKey)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
